// @ts-nocheck
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useTheme } from '../contexts/ThemeContext';
import { Eye, EyeOff, Pin, PinOff, TrendingUp } from 'lucide-react';
import { CoinHeaderCell, StandardHeaderCell, getTableHeaderCellClasses, getStickyPositionStyle } from './TableHeaderCells';
import { ColumnTooltip } from './ColumnTooltip';
import CoinLogoImage from './CoinLogoImage';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

interface BMSBBTCData {
  symbol: string;
  name: string;
  logo_url?: string;
  current_ratio: number;
  current_price_usd: number;
  market_cap: number;
  volume_24h: number;
  sma_20w: number;
  ema_21w: number;
  pct_from_upper: number;
  pct_from_lower: number;
  position: string;
  weeks_since_cross: number;
  cross_direction: string;
  last_updated?: string;
}

interface BullMarketSupportBandBTCProps {
  isOpen?: boolean;
  onToggle?: () => void;
}

const BullMarketSupportBandBTC: React.FC<BullMarketSupportBandBTCProps> = ({ isOpen: isOpenProp, onToggle: onToggleProp }) => {
  const { isDark } = useTheme();
  const [internalIsOpen, setInternalIsOpen] = useState(true);

  // Use internal state if no external control is provided
  const isOpen = isOpenProp !== undefined ? isOpenProp : internalIsOpen;
  const handleToggle = onToggleProp || (() => setInternalIsOpen(!internalIsOpen));

  const [data, setData] = useState<BMSBBTCData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Mock data for fallback when API fails
  const mockData: BMSBBTCData[] = [
    {
      symbol: "ETH",
      name: "Ethereum",
      logo_url: "https://assets.coingecko.com/coins/images/279/small/ethereum.png",
      current_ratio: 0.03974,
      current_price_usd: 2678.90,
      market_cap: 322400000000,
      volume_24h: 15670000000,
      sma_20w: 0.04251,
      ema_21w: 0.04312,
      pct_from_upper: -6.5,
      pct_from_lower: 7.8,
      position: "Within Band",
      weeks_since_cross: 2,
      cross_direction: "Downward",
      last_updated: new Date().toISOString()
    },
    {
      symbol: "SOL",
      name: "Solana",
      logo_url: "https://assets.coingecko.com/coins/images/4128/small/solana.png",
      current_ratio: 0.00146,
      current_price_usd: 98.45,
      market_cap: 46750000000,
      volume_24h: 2847000000,
      sma_20w: 0.00189,
      ema_21w: 0.00195,
      pct_from_upper: -25.1,
      pct_from_lower: 33.6,
      position: "Below Band",
      weeks_since_cross: 5,
      cross_direction: "Downward",
      last_updated: new Date().toISOString()
    },
    {
      symbol: "BNB",
      name: "BNB",
      logo_url: "https://assets.coingecko.com/coins/images/825/small/bnb-icon2_2x.png",
      current_ratio: 0.00883,
      current_price_usd: 595.12,
      market_cap: 86890000000,
      volume_24h: 1890000000,
      sma_20w: 0.00712,
      ema_21w: 0.00738,
      pct_from_upper: 19.6,
      pct_from_lower: -23.9,
      position: "Above Band",
      weeks_since_cross: 7,
      cross_direction: "Upward",
      last_updated: new Date().toISOString()
    },
    {
      symbol: "XRP",
      name: "XRP",
      logo_url: "https://assets.coingecko.com/coins/images/44/small/xrp-symbol-white-128.png",
      current_ratio: 0.0000084,
      current_price_usd: 0.567,
      market_cap: 32450000000,
      volume_24h: 1567000000,
      sma_20w: 0.0000095,
      ema_21w: 0.0000098,
      pct_from_upper: -11.6,
      pct_from_lower: 14.3,
      position: "Within Band",
      weeks_since_cross: 3,
      cross_direction: "Downward",
      last_updated: new Date().toISOString()
    },
    {
      symbol: "ADA",
      name: "Cardano",
      logo_url: "https://assets.coingecko.com/coins/images/975/small/cardano.png",
      current_ratio: 0.0000053,
      current_price_usd: 0.356,
      market_cap: 12450000000,
      volume_24h: 267000000,
      sma_20w: 0.0000066,
      ema_21w: 0.0000068,
      pct_from_upper: -22.1,
      pct_from_lower: 28.4,
      position: "Below Band",
      weeks_since_cross: 6,
      cross_direction: "Downward",
      last_updated: new Date().toISOString()
    },
    {
      symbol: "DOGE",
      name: "Dogecoin",
      logo_url: "https://assets.coingecko.com/coins/images/5/small/dogecoin.png",
      current_ratio: 0.0000016,
      current_price_usd: 0.109,
      market_cap: 16890000000,
      volume_24h: 1240000000,
      sma_20w: 0.0000012,
      ema_21w: 0.0000013,
      pct_from_upper: 23.1,
      pct_from_lower: -30.8,
      position: "Above Band",
      weeks_since_cross: 4,
      cross_direction: "Upward",
      last_updated: new Date().toISOString()
    },
    {
      symbol: "AVAX",
      name: "Avalanche",
      logo_url: "https://assets.coingecko.com/coins/images/12559/small/Avalanche_Circle_RedWhite_Trans.png",
      current_ratio: 0.00041,
      current_price_usd: 27.89,
      market_cap: 11890000000,
      volume_24h: 512000000,
      sma_20w: 0.00048,
      ema_21w: 0.00050,
      pct_from_upper: -14.6,
      pct_from_lower: 21.9,
      position: "Within Band",
      weeks_since_cross: 1,
      cross_direction: "Downward",
      last_updated: new Date().toISOString()
    },
    {
      symbol: "DOT",
      name: "Polkadot",
      logo_url: "https://assets.coingecko.com/coins/images/12171/small/polkadot.png",
      current_ratio: 0.000062,
      current_price_usd: 4.18,
      market_cap: 6120000000,
      volume_24h: 134000000,
      sma_20w: 0.000084,
      ema_21w: 0.000087,
      pct_from_upper: -28.7,
      pct_from_lower: 40.3,
      position: "Below Band",
      weeks_since_cross: 8,
      cross_direction: "Downward",
      last_updated: new Date().toISOString()
    },
    {
      symbol: "LINK",
      name: "Chainlink",
      logo_url: "https://assets.coingecko.com/coins/images/877/small/chainlink-new-logo.png",
      current_ratio: 0.00017,
      current_price_usd: 11.23,
      market_cap: 6980000000,
      volume_24h: 425000000,
      sma_20w: 0.00015,
      ema_21w: 0.00016,
      pct_from_upper: 6.3,
      pct_from_lower: -12.5,
      position: "Above Band",
      weeks_since_cross: 2,
      cross_direction: "Upward",
      last_updated: new Date().toISOString()
    },
    {
      symbol: "MATIC",
      name: "Polygon",
      logo_url: "https://assets.coingecko.com/coins/images/4713/small/matic-token-icon.png",
      current_ratio: 0.0000057,
      current_price_usd: 0.387,
      market_cap: 3780000000,
      volume_24h: 298000000,
      sma_20w: 0.0000074,
      ema_21w: 0.0000076,
      pct_from_upper: -25.0,
      pct_from_lower: 33.3,
      position: "Below Band",
      weeks_since_cross: 4,
      cross_direction: "Downward",
      last_updated: new Date().toISOString()
    }
  ];

  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState<{[key: string]: boolean}>(() => {
    const saved = localStorage.getItem('bmsbBtcColumns');
    return saved ? JSON.parse(saved) : {
      rank: true,
      coin: true,
      ratio: true,
      usdPrice: true,
      position: true,
      pctUpper: true,
      pctLower: true,
      weeksCross: true,
      marketCap: true,
    };
  });

  // Pinned columns state
  const [pinnedColumns, setPinnedColumns] = useState<string[]>(() => {
    const saved = localStorage.getItem('bmsbBtcPinnedColumns');
    return saved ? JSON.parse(saved) : [];
  });
  const tableScrollRef = useRef<HTMLDivElement>(null);

  // Header cell refs for width measurement
  const headerRefs = useRef<Record<string, HTMLTableCellElement | null>>({});

  // Column widths state for sticky positioning
  const [colWidths, setColWidths] = useState<Record<string, number>>({});

  // Define natural column order for consistent pinning
  const PIN_ORDER = [
    'ratio',
    'usdPrice',
    'position',
    'pctUpper',
    'pctLower',
    'weeksCross',
    'marketCap',
  ];

  // Helper function to sort pinned columns according to PIN_ORDER
  const sortPinnedColumns = (columns: string[]): string[] => {
    return PIN_ORDER.filter(col => columns.includes(col));
  };

  // Toast notification for pin limit
  const [showPinLimitToast, setShowPinLimitToast] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchData();
    }
  }, [isOpen]);

  // Add horizontal scroll on wheel
  useEffect(() => {
    const scrollContainer = tableScrollRef.current;
    if (!scrollContainer) return;

    const handleWheel = (e: WheelEvent) => {
      // Check if table has horizontal overflow
      const hasHorizontalScroll = scrollContainer.scrollWidth > scrollContainer.clientWidth;

      if (hasHorizontalScroll) {
        e.preventDefault();
        e.stopPropagation();

        // Convert vertical scroll to horizontal scroll
        const scrollAmount = e.deltaY;
        scrollContainer.scrollLeft += scrollAmount;
      }
    };

    scrollContainer.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      scrollContainer.removeEventListener('wheel', handleWheel);
    };
  }, [data]); // Re-run when data changes

  // Measure column widths on mount and when dependencies change
  useEffect(() => {
    measureColumnWidths();
    const ro = new ResizeObserver(() => measureColumnWidths());
    if (tableScrollRef.current) {
      ro.observe(tableScrollRef.current);
    }
    const handleResize = () => measureColumnWidths();
    window.addEventListener('resize', handleResize);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, [visibleColumns, pinnedColumns, isDark]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token') || localStorage.getItem('access_token');
      const response = await axios.get(
        `${API_BASE}/api/bull-market-support-band-btc?limit=100`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data && response.data.length > 0) {
        setData(response.data);
      } else {
        console.warn('No API data available, using mock data fallback');
        setData(mockData);
      }
    } catch (err: any) {
      console.error('Error fetching BMSB BTC data:', err);
      console.warn('API failed, using mock data fallback');
      setData(mockData);
      setError(null); // Clear error since we're showing mock data
    } finally {
      setLoading(false);
    }
  };

  const toggleColumn = (columnKey: string) => {
    const newVisibleColumns = {
      ...visibleColumns,
      [columnKey]: !visibleColumns[columnKey]
    };
    setVisibleColumns(newVisibleColumns);
    localStorage.setItem('bmsbBtcColumns', JSON.stringify(newVisibleColumns));
  };

  const togglePin = (columnKey: string) => {
    const isPinned = pinnedColumns.includes(columnKey);
    if (isPinned) {
      // Unpin column
      const filteredColumns = pinnedColumns.filter(col => col !== columnKey);
      const newPinnedColumns = sortPinnedColumns(filteredColumns);
      setPinnedColumns(newPinnedColumns);
      localStorage.setItem('bmsbBtcPinnedColumns', JSON.stringify(newPinnedColumns));
    } else {
      // Check limit
      if (pinnedColumns.length >= 6) {
        setShowPinLimitToast(true);
        setTimeout(() => setShowPinLimitToast(false), 3000);
        return;
      }
      // Pin column and sort according to PIN_ORDER
      const unsortedColumns = [...pinnedColumns, columnKey];
      const newPinnedColumns = sortPinnedColumns(unsortedColumns);
      setPinnedColumns(newPinnedColumns);
      localStorage.setItem('bmsbBtcPinnedColumns', JSON.stringify(newPinnedColumns));
    }
  };

  // Measure column widths from header refs
  const measureColumnWidths = () => {
    const keys = ['rank', 'coin', 'ratio', 'usdPrice', 'position', 'pctUpper', 'pctLower', 'weeksCross', 'marketCap'] as const;
    const next: Record<string, number> = {};
    keys.forEach(k => {
      const el = headerRefs.current[k];
      if (el) {
        next[k] = el.getBoundingClientRect().width;
      }
    });
    setColWidths(next);
  };

  const getStickyLeftPosition = (columnKey: string): number => {
    // Ensure we work with the properly ordered pinned columns
    const orderedPinnedColumns = sortPinnedColumns(pinnedColumns);
    const columnIndex = orderedPinnedColumns.indexOf(columnKey);
    if (columnIndex === -1) return 0; // Not pinned

    // Start after the rank and coin columns (which are always sticky)
    let leftPosition = (colWidths.rank || 0) + (colWidths.coin || 0);

    // Add widths of pinned columns that come before this one in PIN_ORDER
    for (let i = 0; i < columnIndex; i++) {
      const prevColumn = orderedPinnedColumns[i];
      leftPosition += colWidths[prevColumn] || 0;
    }

    return leftPosition;
  };

  const formatRatio = (ratio: number) => {
    return ratio.toFixed(8);
  };

  const formatPrice = (price: number) => {
    if (price < 0.01) return `$${price.toFixed(6)}`;
    if (price < 1) return `$${price.toFixed(4)}`;
    return `$${price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatMarketCap = (marketCap: number) => {
    if (marketCap >= 1e12) return `$${(marketCap / 1e12).toFixed(2)}T`;
    if (marketCap >= 1e9) return `$${(marketCap / 1e9).toFixed(2)}B`;
    if (marketCap >= 1e6) return `$${(marketCap / 1e6).toFixed(2)}M`;
    return `$${marketCap.toLocaleString()}`;
  };

  const getPositionColor = (position: string) => {
    switch (position) {
      case 'above':
        return isDark ? 'text-emerald-400' : 'text-green-600';
      case 'inside':
        return isDark ? 'text-yellow-400' : 'text-yellow-600';
      case 'below':
        return isDark ? 'text-red-400' : 'text-red-600';
      default:
        return isDark ? 'text-gray-400' : 'text-gray-600';
    }
  };

  const getPositionIcon = (position: string) => {
    switch (position) {
      case 'above':
        return '🚀';
      case 'inside':
        return '〰️';
      case 'below':
        return '📉';
      default:
        return '❓';
    }
  };

  const getPercentageColor = (pct: number, isUpper: boolean) => {
    if (isUpper) {
      if (pct > 0) return isDark ? 'text-emerald-400' : 'text-green-600';
      if (pct < -10) return isDark ? 'text-red-400' : 'text-red-600';
      return isDark ? 'text-white' : 'text-gray-700';
    } else {
      if (pct > 5) return isDark ? 'text-emerald-400' : 'text-green-600';
      if (pct < 0) return isDark ? 'text-red-400' : 'text-red-600';
      return isDark ? 'text-white' : 'text-gray-700';
    }
  };


  // Calculate summary statistics
  const summary = {
    above: data.filter(d => d.position === 'above').length,
    inside: data.filter(d => d.position === 'inside').length,
    below: data.filter(d => d.position === 'below').length,
    avgWeeksAbove: data.filter(d => d.position === 'above' && d.cross_direction === 'up')
      .reduce((sum, d) => sum + d.weeks_since_cross, 0) /
      (data.filter(d => d.position === 'above' && d.cross_direction === 'up').length || 1),
  };

  return (
    <div className={`rounded-2xl shadow-sm border mb-6 transition-all duration-200 ${
      isDark
        ? 'bg-white/[0.03] border-white/10'
        : 'bg-white border-gray-200'
    }`}>
      {/* Header */}
      <div
        className={`px-6 py-4 border-b cursor-pointer transition-colors duration-200 ${
          isDark
            ? 'border-white/10 hover:bg-white/5'
            : 'border-gray-200 hover:bg-gray-50'
        }`}
        onClick={handleToggle}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-lg ${isDark ? 'bg-emerald-500/10' : 'bg-emerald-50'}`}>
              <TrendingUp className={`${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} size={24} />
            </div>
            <div>
              <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Bull Market Support Band (BTC Ratio)
              </h3>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                20-week SMA & 21-week EMA on coin/BTC ratio (10 years data) • Outperformance vs Bitcoin
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            {data.length > 0 && (
              <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                <span className="font-medium">{data.length}</span> coins analyzed
              </div>
            )}
            <div className="flex items-center space-x-2">
              <div className="group relative">
                <span className={`text-lg cursor-help ${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}>ℹ️</span>
                <div className={`absolute bottom-full right-0 mb-2 w-80 p-3 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10 ${
                  isDark ? 'bg-gray-800 text-white' : 'bg-gray-900 text-white'
                }`}>
                  <div className="font-medium mb-1">Bull Market Support Band (BTC Ratio):</div>
                  <div className="text-xs space-y-1">
                    <div>• <strong>Above Band (🚀):</strong> Coin/BTC ratio above support - Outperforming Bitcoin</div>
                    <div>• <strong>Within Band (〰️):</strong> Ratio between support levels - Mixed performance vs BTC</div>
                    <div>• <strong>Below Band (📉):</strong> Ratio below support - Underperforming Bitcoin</div>
                    <div>• <strong>20W SMA:</strong> 20-week Simple Moving Average of BTC ratio</div>
                    <div>• <strong>21W EMA:</strong> 21-week Exponential Moving Average of BTC ratio</div>
                  </div>
                </div>
              </div>
              <svg
                className={`w-5 h-5 transform transition-transform ${isOpen ? 'rotate-180' : ''} ${isDark ? 'text-gray-400' : 'text-gray-600'}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      {isOpen && (
        <div className="max-h-none overflow-hidden">
          {loading ? (
            <div className="px-6 py-12 text-center">
              <div className={`inline-block animate-spin rounded-full h-8 w-8 border-b-2 ${isDark ? 'border-emerald-400' : 'border-orange-600'}`}></div>
              <p className={`mt-2 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Loading Bull Market Support Band (BTC ratio) data...</p>
            </div>
          ) : error ? (
            <div className={`px-6 py-8 text-center ${isDark ? 'text-red-400' : 'text-red-600'}`}>
              <p>⚠️ {error}</p>
            </div>
          ) : (
            <>
              {/* Summary Stats */}
              {data.length > 0 && (
                <div className={`px-6 py-4 border-b ${isDark ? 'bg-white/[0.03] border-white/[0.05]' : 'bg-gray-50 border-gray-200'}`}>
                  <div className="grid grid-cols-4 gap-4">
                    <div className="text-center">
                      <div className={`text-2xl font-bold ${isDark ? 'text-emerald-400' : 'text-green-600'}`}>{summary.above}</div>
                      <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Above Band<br/>(Outperforming BTC)</div>
                    </div>
                    <div className="text-center">
                      <div className={`text-2xl font-bold ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`}>{summary.inside}</div>
                      <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Inside Band<br/>(Testing Support)</div>
                    </div>
                    <div className="text-center">
                      <div className={`text-2xl font-bold ${isDark ? 'text-red-400' : 'text-red-600'}`}>{summary.below}</div>
                      <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Below Band<br/>(Underperforming)</div>
                    </div>
                    <div className="text-center">
                      <div className={`text-2xl font-bold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{summary.avgWeeksAbove.toFixed(0)}</div>
                      <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Avg Weeks Above</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Table */}
              <div
                ref={tableScrollRef}
                className={`overflow-x-auto custom-scrollbar ${!isDark ? 'light-scrollbar' : ''} relative`}
                title="Use mouse wheel to scroll horizontally"
                style={{
                  scrollbarWidth: 'thin',
                  scrollbarColor: isDark ? '#4b5563 #1f2937' : '#d1d5db #f3f4f6'
                }}
              >
                <table className="w-full">
                  <thead className={isDark ? 'border-b border-white/10 text-xs uppercase tracking-wide' : 'bg-gray-50 border-b border-gray-200 text-xs uppercase tracking-wide'}>
                    <tr>
                      <th
                        ref={(el) => (headerRefs.current.rank = el)}
                        className={getTableHeaderCellClasses(visibleColumns.rank, false, isDark)}
                        style={{
                          position: 'sticky',
                          left: 0,
                          top: 0,
                          zIndex: 2,
                          background: isDark ? '#09090b' : '#ffffff',
                          minWidth: '80px',
                          width: '80px'
                        }}
                      >
                        <CoinHeaderCell title="Rank" />
                      </th>
                      <th
                        ref={(el) => (headerRefs.current.coin = el)}
                        className={getTableHeaderCellClasses(visibleColumns.coin, false, isDark)}
                        style={{
                          width: visibleColumns.coin ? '180px' : '32px',
                          minWidth: visibleColumns.coin ? '180px' : '32px',
                          position: 'sticky',
                          left: '80px',
                          top: 0,
                          zIndex: 2,
                          background: isDark ? '#09090b' : '#ffffff'
                        }}
                      >
                        {visibleColumns.coin ? (
                          <CoinHeaderCell title="Coin" />
                        ) : (
                          <StandardHeaderCell
                            colKey="coin"
                            label="Coin"
                            abbrev="C"
                            sortableIcon={false}
                            visible={visibleColumns.coin}
                            pinned={false}
                            onToggleColumn={toggleColumn}
                            onTogglePin={togglePin}
                          />
                        )}
                      </th>
                      <th
                        ref={(el) => (headerRefs.current.ratio = el)}
                        className={getTableHeaderCellClasses(visibleColumns.ratio, pinnedColumns.includes('ratio'), isDark)}
                        style={getStickyPositionStyle(pinnedColumns.includes('ratio'), getStickyLeftPosition('ratio'), isDark)}
                      >
                        <StandardHeaderCell
                          colKey="ratio"
                          label="BTC Ratio"
                          abbrev="Ratio"
                          visible={visibleColumns.ratio}
                          pinned={pinnedColumns.includes('ratio')}
                          onToggleColumn={toggleColumn}
                          onTogglePin={togglePin}
                        />
                      </th>
                      <th
                        ref={(el) => (headerRefs.current.usdPrice = el)}
                        className={getTableHeaderCellClasses(visibleColumns.usdPrice, pinnedColumns.includes('usdPrice'), isDark)}
                        style={getStickyPositionStyle(pinnedColumns.includes('usdPrice'), getStickyLeftPosition('usdPrice'), isDark)}
                      >
                        <StandardHeaderCell
                          colKey="usdPrice"
                          label="USD Price"
                          abbrev="USD"
                          visible={visibleColumns.usdPrice}
                          pinned={pinnedColumns.includes('usdPrice')}
                          onToggleColumn={toggleColumn}
                          onTogglePin={togglePin}
                        />
                      </th>
                      <th
                        ref={(el) => (headerRefs.current.position = el)}
                        className={getTableHeaderCellClasses(visibleColumns.position, pinnedColumns.includes('position'), isDark)}
                        style={getStickyPositionStyle(pinnedColumns.includes('position'), getStickyLeftPosition('position'), isDark)}
                      >
                        <StandardHeaderCell
                          colKey="position"
                          label="Position"
                          abbrev="Pos"
                          visible={visibleColumns.position}
                          pinned={pinnedColumns.includes('position')}
                          onToggleColumn={toggleColumn}
                          onTogglePin={togglePin}
                        />
                      </th>
                      <th
                        ref={(el) => (headerRefs.current.pctUpper = el)}
                        className={getTableHeaderCellClasses(visibleColumns.pctUpper, pinnedColumns.includes('pctUpper'), isDark)}
                        style={getStickyPositionStyle(pinnedColumns.includes('pctUpper'), getStickyLeftPosition('pctUpper'), isDark)}
                      >
                        <StandardHeaderCell
                          colKey="pctUpper"
                          label="% from Upper"
                          abbrev="Upper"
                          secondary="20W SMA"
                          visible={visibleColumns.pctUpper}
                          pinned={pinnedColumns.includes('pctUpper')}
                          onToggleColumn={toggleColumn}
                          onTogglePin={togglePin}
                        />
                      </th>
                      <th
                        ref={(el) => (headerRefs.current.pctLower = el)}
                        className={getTableHeaderCellClasses(visibleColumns.pctLower, pinnedColumns.includes('pctLower'), isDark)}
                        style={getStickyPositionStyle(pinnedColumns.includes('pctLower'), getStickyLeftPosition('pctLower'), isDark)}
                      >
                        <StandardHeaderCell
                          colKey="pctLower"
                          label="% from Lower"
                          abbrev="Lower"
                          secondary="21W EMA"
                          visible={visibleColumns.pctLower}
                          pinned={pinnedColumns.includes('pctLower')}
                          onToggleColumn={toggleColumn}
                          onTogglePin={togglePin}
                        />
                      </th>
                      <th
                        ref={(el) => (headerRefs.current.weeksCross = el)}
                        className={getTableHeaderCellClasses(visibleColumns.weeksCross, pinnedColumns.includes('weeksCross'), isDark)}
                        style={getStickyPositionStyle(pinnedColumns.includes('weeksCross'), getStickyLeftPosition('weeksCross'), isDark)}
                      >
                        <StandardHeaderCell
                          colKey="weeksCross"
                          label="Weeks Since Crossover"
                          abbrev="Weeks"
                          secondary="Crossover"
                          visible={visibleColumns.weeksCross}
                          pinned={pinnedColumns.includes('weeksCross')}
                          onToggleColumn={toggleColumn}
                          onTogglePin={togglePin}
                        />
                      </th>
                      <th
                        ref={(el) => (headerRefs.current.marketCap = el)}
                        className={getTableHeaderCellClasses(visibleColumns.marketCap, pinnedColumns.includes('marketCap'), isDark)}
                        style={getStickyPositionStyle(pinnedColumns.includes('marketCap'), getStickyLeftPosition('marketCap'), isDark)}
                      >
                        <StandardHeaderCell
                          colKey="marketCap"
                          label="Market Cap"
                          abbrev="MCap"
                          visible={visibleColumns.marketCap}
                          pinned={pinnedColumns.includes('marketCap')}
                          onToggleColumn={toggleColumn}
                          onTogglePin={togglePin}
                        />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...data].sort((a, b) => b.pct_from_upper - a.pct_from_upper).map((coin, index) => (
                      <tr key={coin.symbol} className={`${isDark ? 'hover:bg-white/[0.05] border-b border-white/[0.05]' : 'hover:bg-gray-50 border-b border-gray-200'}`}>
                        <td
                          className={`px-4 py-4 whitespace-nowrap text-sm font-medium sticky left-0 z-10 ${isDark ? 'text-white' : 'text-gray-900'}`}
                          style={{
                            position: 'sticky',
                            left: 0,
                            top: 0,
                            zIndex: 2,
                            background: isDark ? '#09090b' : '#ffffff',
                            width: '80px',
                            minWidth: '80px'
                          }}
                        >
                          #{index + 1}
                        </td>
                        <td
                          className={`px-3 py-2 transition-all duration-300 ${visibleColumns.coin ? '' : 'w-8 max-w-8'}`}
                          style={{
                            width: visibleColumns.coin ? '180px' : '32px',
                            minWidth: visibleColumns.coin ? '180px' : '32px',
                            position: 'sticky',
                            left: '80px',
                            top: 0,
                            zIndex: 2,
                            background: isDark ? '#09090b' : '#ffffff'
                          }}
                        >
                          {visibleColumns.coin ? (
                            <div className="flex items-center gap-2">
                              <CoinLogoImage
                                src={coin.logo_url || ''}
                                alt={coin.symbol}
                                size="md"
                                className="mr-3"
                              />
                              <div>
                                <div className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                  {coin.name && coin.name !== coin.symbol ? `${coin.name} (${coin.symbol})` : coin.symbol}
                                </div>
                              </div>
                            </div>
                          ) : (
                            <ColumnTooltip
                              content={
                                <div className="flex items-center gap-2">
                                  <CoinLogoImage
                                    src={coin.logo_url || ''}
                                    alt={coin.symbol}
                                    size="md"
                                    className="mr-3"
                                  />
                                  <div>
                                    <div className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                      {coin.name && coin.name !== coin.symbol ? `${coin.name} (${coin.symbol})` : coin.symbol}
                                    </div>
                                  </div>
                                </div>
                              }
                              onColumnClick={() => toggleColumn('coin')}
                            >
                              <div className="text-center text-gray-500 text-xs">•</div>
                            </ColumnTooltip>
                          )}
                        </td>
                        <td
                          className={`px-3 py-2 text-center transition-all duration-300 ${
                            visibleColumns.ratio ? '' : 'w-8 max-w-8'
                          } ${pinnedColumns.includes('ratio') ? 'sticky z-10' : ''}`}
                          style={{
                            ...(pinnedColumns.includes('ratio') ? { left: `${getStickyLeftPosition('ratio')}px`, background: isDark ? '#09090b' : '#ffffff' } : {})
                          }}
                        >
                          {visibleColumns.ratio ? (
                            <span className={`text-sm font-mono ${isDark ? 'text-white' : 'text-gray-900'}`}>
                              {formatRatio(coin.current_ratio)}
                            </span>
                          ) : (
                            <ColumnTooltip
                              content={
                                <span className={`text-sm font-mono ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                  {formatRatio(coin.current_ratio)}
                                </span>
                              }
                              onColumnClick={() => toggleColumn('ratio')}
                            >
                              <div className="text-center text-gray-500 text-xs">•</div>
                            </ColumnTooltip>
                          )}
                        </td>
                        <td
                          className={`px-3 py-2 text-center transition-all duration-300 ${
                            visibleColumns.usdPrice ? '' : 'w-8 max-w-8'
                          } ${pinnedColumns.includes('usdPrice') ? 'sticky z-10' : ''}`}
                          style={{
                            ...(pinnedColumns.includes('usdPrice') ? { left: `${getStickyLeftPosition('usdPrice')}px`, background: isDark ? '#09090b' : '#ffffff' } : {})
                          }}
                        >
                          {visibleColumns.usdPrice ? (
                            <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                              {formatPrice(coin.current_price_usd)}
                            </span>
                          ) : (
                            <ColumnTooltip
                              content={
                                <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                  {formatPrice(coin.current_price_usd)}
                                </span>
                              }
                              onColumnClick={() => toggleColumn('usdPrice')}
                            >
                              <div className="text-center text-gray-500 text-xs">•</div>
                            </ColumnTooltip>
                          )}
                        </td>
                        <td
                          className={`px-3 py-2 text-center transition-all duration-300 ${
                            visibleColumns.position ? '' : 'w-8 max-w-8'
                          } ${pinnedColumns.includes('position') ? 'sticky z-10' : ''}`}
                          style={{
                            ...(pinnedColumns.includes('position') ? { left: `${getStickyLeftPosition('position')}px`, background: isDark ? '#09090b' : '#ffffff' } : {})
                          }}
                        >
                          {visibleColumns.position ? (
                            <span className={`text-xs font-medium ${getPositionColor(coin.position)}`}>
                              <span className="mr-1">{getPositionIcon(coin.position)}</span>
                              {coin.position.charAt(0).toUpperCase() + coin.position.slice(1)}
                            </span>
                          ) : (
                            <ColumnTooltip
                              content={
                                <span className={`text-xs font-medium ${getPositionColor(coin.position)}`}>
                                  <span className="mr-1">{getPositionIcon(coin.position)}</span>
                                  {coin.position.charAt(0).toUpperCase() + coin.position.slice(1)}
                                </span>
                              }
                              onColumnClick={() => toggleColumn('position')}
                            >
                              <div className="text-center text-gray-500 text-xs">•</div>
                            </ColumnTooltip>
                          )}
                        </td>
                        <td
                          className={`px-3 py-2 text-center transition-all duration-300 ${
                            visibleColumns.pctUpper ? '' : 'w-8 max-w-8'
                          } ${pinnedColumns.includes('pctUpper') ? 'sticky z-10' : ''}`}
                          style={{
                            ...(pinnedColumns.includes('pctUpper') ? { left: `${getStickyLeftPosition('pctUpper')}px`, background: isDark ? '#09090b' : '#ffffff' } : {})
                          }}
                        >
                          {visibleColumns.pctUpper ? (
                            <span className={`text-sm font-semibold ${getPercentageColor(coin.pct_from_upper, true)}`}>
                              {coin.pct_from_upper > 0 ? '+' : ''}{coin.pct_from_upper.toFixed(2)}%
                            </span>
                          ) : (
                            <ColumnTooltip
                              content={
                                <span className={`text-sm font-semibold ${getPercentageColor(coin.pct_from_upper, true)}`}>
                                  {coin.pct_from_upper > 0 ? '+' : ''}{coin.pct_from_upper.toFixed(2)}%
                                </span>
                              }
                              onColumnClick={() => toggleColumn('pctUpper')}
                            >
                              <div className="text-center text-gray-500 text-xs">•</div>
                            </ColumnTooltip>
                          )}
                        </td>
                        <td
                          className={`px-3 py-2 text-center transition-all duration-300 ${
                            visibleColumns.pctLower ? '' : 'w-8 max-w-8'
                          } ${pinnedColumns.includes('pctLower') ? 'sticky z-10' : ''}`}
                          style={{
                            ...(pinnedColumns.includes('pctLower') ? { left: `${getStickyLeftPosition('pctLower')}px`, background: isDark ? '#09090b' : '#ffffff' } : {})
                          }}
                        >
                          {visibleColumns.pctLower ? (
                            <span className={`text-sm font-semibold ${getPercentageColor(coin.pct_from_lower, false)}`}>
                              {coin.pct_from_lower > 0 ? '+' : ''}{coin.pct_from_lower.toFixed(2)}%
                            </span>
                          ) : (
                            <ColumnTooltip
                              content={
                                <span className={`text-sm font-semibold ${getPercentageColor(coin.pct_from_lower, false)}`}>
                                  {coin.pct_from_lower > 0 ? '+' : ''}{coin.pct_from_lower.toFixed(2)}%
                                </span>
                              }
                              onColumnClick={() => toggleColumn('pctLower')}
                            >
                              <div className="text-center text-gray-500 text-xs">•</div>
                            </ColumnTooltip>
                          )}
                        </td>
                        <td
                          className={`px-3 py-2 text-center transition-all duration-300 ${
                            visibleColumns.weeksCross ? '' : 'w-8 max-w-8'
                          } ${pinnedColumns.includes('weeksCross') ? 'sticky z-10' : ''}`}
                          style={{
                            ...(pinnedColumns.includes('weeksCross') ? { left: `${getStickyLeftPosition('weeksCross')}px`, background: isDark ? '#09090b' : '#ffffff' } : {})
                          }}
                        >
                          {visibleColumns.weeksCross ? (
                            <div>
                              <div className={`text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                {coin.weeks_since_cross} weeks
                              </div>
                              <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                                ({coin.cross_direction === 'up' ? '↑' : '↓'} {coin.cross_direction})
                              </div>
                            </div>
                          ) : (
                            <ColumnTooltip
                              content={
                                <div>
                                  <div className={`text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                    {coin.weeks_since_cross} weeks
                                  </div>
                                  <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                                    ({coin.cross_direction === 'up' ? '↑' : '↓'} {coin.cross_direction})
                                  </div>
                                </div>
                              }
                              onColumnClick={() => toggleColumn('weeksCross')}
                            >
                              <div className="text-center text-gray-500 text-xs">•</div>
                            </ColumnTooltip>
                          )}
                        </td>
                        <td
                          className={`px-3 py-2 text-center transition-all duration-300 ${
                            visibleColumns.marketCap ? '' : 'w-8 max-w-8'
                          } ${pinnedColumns.includes('marketCap') ? 'sticky z-10' : ''}`}
                          style={{
                            ...(pinnedColumns.includes('marketCap') ? { left: `${getStickyLeftPosition('marketCap')}px`, background: isDark ? '#09090b' : '#ffffff' } : {})
                          }}
                        >
                          {visibleColumns.marketCap ? (
                            <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                              {formatMarketCap(coin.market_cap)}
                            </span>
                          ) : (
                            <ColumnTooltip
                              content={
                                <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                  {formatMarketCap(coin.market_cap)}
                                </span>
                              }
                              onColumnClick={() => toggleColumn('marketCap')}
                            >
                              <div className="text-center text-gray-500 text-xs">•</div>
                            </ColumnTooltip>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Info Footer */}
              <div className={`px-6 py-4 border-t ${isDark ? 'bg-blue-900/30 border-blue-400/30' : 'bg-blue-50 border-blue-100'}`}>
                <div className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  <p className="mb-2"><strong>What does BTC Ratio Bull Market Support Band show?</strong></p>
                  <p className="mb-1">• <strong>Above Band (🚀):</strong> Coin/BTC ratio above support - Outperforming Bitcoin</p>
                  <p className="mb-1">• <strong>Inside Band (〰️):</strong> Ratio between support levels - Mixed performance vs BTC</p>
                  <p className="mb-1">• <strong>Below Band (📉):</strong> Ratio below support - Underperforming Bitcoin</p>
                  <p className={`mt-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>💡 This helps identify altcoins in strong uptrends relative to Bitcoin. Coins above the band are gaining BTC value.</p>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Toast notification for pin limit */}
      {showPinLimitToast && (
        <div
          className={`fixed bottom-4 right-4 px-4 py-3 rounded-lg shadow-lg z-50 ${
            isDark
              ? 'bg-red-500/90 text-white border border-red-400'
              : 'bg-red-100 text-red-900 border border-red-300'
          }`}
          role="alert"
        >
          Limit reached (6). Unpin another column to continue.
        </div>
      )}
    </div>
  );
};

export default BullMarketSupportBandBTC;
